import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, ExternalLink, Sparkles } from "lucide-react";
import { useTokensCatalog } from "@/hooks/useTokensCatalog";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface TickerMentionCardProps {
  ticker: string;
  variant?: "inline" | "card" | "compact";
  showSparkline?: boolean;
}

interface TokenData {
  ticker: string;
  name: string;
  logo_url?: string;
  price?: number;
  price_change_24h?: number;
  market_cap?: number;
  sector?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Mini Sparkline
// ─────────────────────────────────────────────────────────────────────────────

const MiniSparkline: React.FC<{ 
  trend: "up" | "down" | "neutral";
  color: string;
}> = ({ trend, color }) => {
  // Generate a simple sparkline path based on trend
  const points = trend === "up" 
    ? "0,20 10,18 20,15 30,12 40,14 50,8 60,5"
    : trend === "down"
      ? "0,5 10,8 20,10 30,15 40,12 50,18 60,20"
      : "0,12 10,14 20,11 30,13 40,12 50,14 60,12";

  return (
    <svg 
      viewBox="0 0 60 25" 
      className="w-14 h-5"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id={`spark-grad-${trend}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polygon
        points={`${points} 60,25 0,25`}
        fill={`url(#spark-grad-${trend})`}
      />
    </svg>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Format Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatPrice(price?: number): string {
  if (price === undefined || price === null) return "—";
  if (price >= 1000) return `$${(price / 1000).toFixed(2)}K`;
  if (price >= 1) return `$${price.toFixed(2)}`;
  if (price >= 0.01) return `$${price.toFixed(4)}`;
  return `$${price.toFixed(6)}`;
}

function formatMarketCap(mcap?: number): string {
  if (mcap === undefined || mcap === null) return "—";
  if (mcap >= 1e12) return `$${(mcap / 1e12).toFixed(2)}T`;
  if (mcap >= 1e9) return `$${(mcap / 1e9).toFixed(2)}B`;
  if (mcap >= 1e6) return `$${(mcap / 1e6).toFixed(2)}M`;
  return `$${mcap.toLocaleString()}`;
}

function formatChange(change?: number): string {
  if (change === undefined || change === null) return "—";
  const sign = change >= 0 ? "+" : "";
  return `${sign}${change.toFixed(2)}%`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Inline Variant (for inline text mentions)
// ─────────────────────────────────────────────────────────────────────────────

const InlineVariant: React.FC<{ token: TokenData }> = ({ token }) => {
  const change = token.price_change_24h ?? 0;
  const isPositive = change >= 0;
  const trend = change > 0.5 ? "up" : change < -0.5 ? "down" : "neutral";

  return (
    <Link
      to={`/app/tokens/${token.ticker.toLowerCase()}`}
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-800/60 hover:bg-gray-700/60 border border-gray-700/50 hover:border-gray-600/50 transition-all group"
    >
      {token.logo_url ? (
        <img 
          src={token.logo_url} 
          alt={token.ticker}
          className="h-4 w-4 rounded-full"
          onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
        />
      ) : (
        <div className="h-4 w-4 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
          <span className="text-[8px] font-bold text-white">{token.ticker[0]}</span>
        </div>
      )}
      <span className="text-sm font-medium text-white">${token.ticker}</span>
      {token.price !== undefined && (
        <>
          <span className="text-xs text-gray-400">{formatPrice(token.price)}</span>
          <span className={`text-xs font-medium ${isPositive ? "text-emerald-400" : "text-rose-400"}`}>
            {formatChange(change)}
          </span>
          <MiniSparkline trend={trend} color={isPositive ? "#10b981" : "#f43f5e"} />
        </>
      )}
      <ExternalLink className="h-3 w-3 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
    </Link>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Card Variant (for hover previews or expanded views)
// ─────────────────────────────────────────────────────────────────────────────

const CardVariant: React.FC<{ token: TokenData }> = ({ token }) => {
  const change = token.price_change_24h ?? 0;
  const isPositive = change >= 0;
  const trend = change > 0.5 ? "up" : change < -0.5 ? "down" : "neutral";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      className="relative rounded-xl border border-gray-700/50 bg-gradient-to-br from-gray-900/90 to-gray-800/90 backdrop-blur-xl overflow-hidden p-4 w-64"
    >
      {/* Background glow */}
      <div 
        className="absolute -top-10 -right-10 h-20 w-20 rounded-full blur-2xl"
        style={{
          background: isPositive 
            ? "radial-gradient(circle, rgba(16, 185, 129, 0.2) 0%, transparent 70%)"
            : "radial-gradient(circle, rgba(244, 63, 94, 0.2) 0%, transparent 70%)",
        }}
      />

      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          {token.logo_url ? (
            <img 
              src={token.logo_url} 
              alt={token.ticker}
              className="h-10 w-10 rounded-xl"
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
          ) : (
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-white">{token.ticker}</span>
              {token.sector && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-700/50 text-gray-400">
                  {token.sector}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 truncate">{token.name}</p>
          </div>
        </div>

        {/* Price Row */}
        <div className="flex items-end justify-between mb-3">
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Price</p>
            <p className="text-xl font-semibold text-white">{formatPrice(token.price)}</p>
          </div>
          <div className="text-right">
            <div className={`flex items-center gap-1 ${isPositive ? "text-emerald-400" : "text-rose-400"}`}>
              {isPositive ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
              <span className="text-lg font-semibold">{formatChange(change)}</span>
            </div>
            <p className="text-xs text-gray-500">24h</p>
          </div>
        </div>

        {/* Sparkline */}
        <div className="h-12 mb-3">
          <svg 
            viewBox="0 0 200 50" 
            className="w-full h-full"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="card-sparkline-gradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={isPositive ? "#10b981" : "#f43f5e"} stopOpacity="0.3" />
                <stop offset="100%" stopColor={isPositive ? "#10b981" : "#f43f5e"} stopOpacity="0" />
              </linearGradient>
            </defs>
            <path
              d={trend === "up" 
                ? "M0,40 Q25,35 50,30 T100,25 T150,15 T200,10"
                : trend === "down"
                  ? "M0,10 Q25,15 50,20 T100,30 T150,35 T200,40"
                  : "M0,25 Q25,20 50,28 T100,22 T150,26 T200,24"
              }
              fill="none"
              stroke={isPositive ? "#10b981" : "#f43f5e"}
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d={`${trend === "up" 
                ? "M0,40 Q25,35 50,30 T100,25 T150,15 T200,10"
                : trend === "down"
                  ? "M0,10 Q25,15 50,20 T100,30 T150,35 T200,40"
                  : "M0,25 Q25,20 50,28 T100,22 T150,26 T200,24"
              } L200,50 L0,50 Z`}
              fill="url(#card-sparkline-gradient)"
            />
          </svg>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-lg bg-gray-800/50 px-2 py-1.5">
            <span className="text-gray-500">Market Cap</span>
            <p className="font-medium text-white">{formatMarketCap(token.market_cap)}</p>
          </div>
          <Link
            to={`/app/tokens/${token.ticker.toLowerCase()}`}
            className="rounded-lg bg-violet-500/20 hover:bg-violet-500/30 border border-violet-500/30 px-2 py-1.5 text-center transition-colors"
          >
            <span className="text-violet-300 font-medium">View Details →</span>
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Compact Variant (for lists)
// ─────────────────────────────────────────────────────────────────────────────

const CompactVariant: React.FC<{ token: TokenData }> = ({ token }) => {
  const change = token.price_change_24h ?? 0;
  const isPositive = change >= 0;

  return (
    <Link
      to={`/app/tokens/${token.ticker.toLowerCase()}`}
      className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-800/50 transition-colors"
    >
      {token.logo_url ? (
        <img 
          src={token.logo_url} 
          alt={token.ticker}
          className="h-5 w-5 rounded-full"
        />
      ) : (
        <div className="h-5 w-5 rounded-full bg-gray-700 flex items-center justify-center">
          <span className="text-[9px] font-bold text-gray-400">{token.ticker[0]}</span>
        </div>
      )}
      <span className="text-sm font-medium text-white flex-1">{token.ticker}</span>
      <span className="text-xs text-gray-400">{formatPrice(token.price)}</span>
      <span className={`text-xs font-medium ${isPositive ? "text-emerald-400" : "text-rose-400"}`}>
        {formatChange(change)}
      </span>
    </Link>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const TickerMentionCard: React.FC<TickerMentionCardProps> = ({
  ticker,
  variant = "inline",
}) => {
  // Fetch token data from catalog
  const { data: catalogData } = useTokensCatalog();
  
  // Find token in catalog
  const token = React.useMemo(() => {
    if (!catalogData || !Array.isArray(catalogData)) return null;
    const upperTicker = ticker.toUpperCase();
    const found = catalogData.find(
      (t) => t.ticker.toUpperCase() === upperTicker
    );
    if (!found) return null;
    // TokenMetadata from catalog only has basic info - price data would need separate fetch
    return {
      ticker: found.ticker,
      name: found.name || found.ticker,
      logo_url: found.logo_url ?? undefined,
    } as TokenData;
  }, [catalogData, ticker]);

  // Loading/not found state
  if (!token) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-800/50 text-sm text-violet-400 font-medium">
        ${ticker}
      </span>
    );
  }

  switch (variant) {
    case "card":
      return <CardVariant token={token} />;
    case "compact":
      return <CompactVariant token={token} />;
    case "inline":
    default:
      return <InlineVariant token={token} />;
  }
};

export default TickerMentionCard;

